/**
 * The words the capture flow uses: one next step at a time from a fixed
 * vocabulary, the per-phase copy, the headband badge and the step rail.
 * Carried from Canvas 05 §02/§01 via `calibrations/static/capture/guidance.mjs`.
 *
 * Sprint 1.9.7 flips fidelity entry 1 back: the product processes the session
 * itself, so the accepted screen MAY promise Today. There is no archive, no
 * path and no upload in any sentence here.
 *
 * Pure: no DOM.
 */

import { RECORD_LENGTH_TEXT, RECORD_TIMER_TEXT, okSet, tierOf, type Tier } from './quality'

export const PHASES = Object.freeze([
  'off',
  'unsupported',
  'scanning',
  'fitting',
  'stale',
  'ready',
  'recording',
  'journal',
  'processing',
  'kept',
  'sending',
  'scored',
  'unscored',
  'lost',
] as const)

export type Phase = (typeof PHASES)[number]
/**
 * Why the recording ended short. `package_refused` (1.9.13 W2): block 1 of a
 * paired session failed its self-check, so the pair was abandoned and block 2
 * stopped; the headband was fine.
 */
export type LostReason = 'lost' | 'stopped' | 'package_refused'

/** Guidance keys are a closed set (plus a channel suffix for none/weak). */
export const GUIDANCE_KEYS = Object.freeze(['stale', 'none', 'weak', 'settle', 'ready-weak', 'ready'] as const)

export const STEPS = Object.freeze([
  'Connected',
  'Contacts checked',
  'Live signal ready',
  'Recording',
  'Checked after',
  'Daily Status',
] as const)

export interface GuidanceContact {
  channel: string
  loc: string
  kohm: number | null | undefined
}

export interface Guidance {
  key: string
  eyebrow: string
  title: string
  body: string
  focus: string | null
  adjust: boolean
}

const lc = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)

/**
 * guide({ phase, contacts }) where contacts are in display order.
 * Returns one next step. Never names a cause.
 */
export function guide({ phase, contacts }: { phase: Phase; contacts: readonly GuidanceContact[] }): Guidance {
  if (phase === 'stale') {
    return {
      key: 'stale',
      eyebrow: 'Waiting',
      title: 'Waiting for a fresh reading',
      body: 'The last reading is still shown, but it does not count as current. If nothing arrives, check that the headband is on and connected.',
      focus: null,
      adjust: false,
    }
  }
  const list = contacts ?? []
  const tiers: Tier[] = list.map((c) => tierOf(c.kohm))
  const noneIdx = tiers.indexOf('none')
  if (noneIdx >= 0) {
    const c = list[noneIdx]!
    return {
      key: 'none:' + c.channel,
      eyebrow: 'Next step',
      title: `Adjust the ${lc(c.loc)} contact`,
      body: 'It is not touching skin. Lift the band slightly there, set it back down, then hold still for a fresh reading.',
      focus: c.channel,
      adjust: true,
    }
  }
  if (!okSet(tiers)) {
    // The weakest contact below Usable.
    let w = -1
    list.forEach((c, i) => {
      if (tiers[i] === 'weak' && (w < 0 || (c.kohm ?? 0) > (list[w]!.kohm ?? 0))) w = i
    })
    if (w < 0) w = tiers.indexOf('weak')
    const c = list[Math.max(0, w)]!
    return {
      key: 'weak:' + c.channel,
      eyebrow: 'Next step',
      title: `Press the ${lc(c.loc)} contact a little firmer`,
      body: 'Then hold still for a fresh reading.',
      focus: c.channel,
      adjust: true,
    }
  }
  if (phase !== 'ready') {
    return {
      key: 'settle',
      eyebrow: 'Almost',
      title: 'Hold still',
      body: 'Confirming a steady signal across all contacts.',
      focus: null,
      adjust: false,
    }
  }
  const weakIdx = tiers.indexOf('weak')
  if (weakIdx >= 0) {
    const c = list[weakIdx]!
    return {
      key: 'ready-weak',
      eyebrow: 'Ready',
      title: 'Live signal ready',
      body: `The ${lc(c.loc)} contact is weak and will count for less. You can start now, or press it a little firmer first.`,
      focus: c.channel,
      adjust: true,
    }
  }
  return {
    key: 'ready',
    eyebrow: 'Ready',
    title: 'Live signal ready',
    body: `Every contact is touching skin and the signal is steady. Keep your eyes closed and stay still for ${RECORD_LENGTH_TEXT}.`,
    focus: null,
    adjust: false,
  }
}

/** Whether a guidance key belongs to the fixed vocabulary. */
export function isKnownGuidanceKey(key: string): boolean {
  const base = String(key).split(':')[0] ?? ''
  return (GUIDANCE_KEYS as readonly string[]).includes(base)
}

export type PillTone = 'neutral' | 'cyan' | 'green' | 'amber' | 'red'

export interface PhaseCopy {
  title: string
  lead: string
  pill: string
  tone: PillTone
}

/**
 * Title, lead and phase pill per phase. `ctx.reason` is 'lost' | 'stopped'
 * for the lost phase.
 */
export function copyFor(phase: Phase, ctx: { reason?: LostReason | null } = {}): PhaseCopy {
  const C: Record<Phase, PhaseCopy> = {
    off: {
      title: 'Connect your headband',
      lead: 'Three checks before a recording: the headband is connected, every contact touches skin, and the live signal is steady.',
      pill: 'Not connected',
      tone: 'neutral',
    },
    unsupported: {
      title: 'This browser cannot talk to the headband',
      lead: 'Recording needs Web Bluetooth, which Chrome and Edge provide on a computer or an Android phone. Safari and Firefox do not.',
      pill: 'Not supported here',
      tone: 'amber',
    },
    scanning: {
      title: 'Looking for your headband',
      lead: 'Choose it in the browser’s Bluetooth dialog. Keep it switched on and within arm’s reach.',
      pill: 'Looking…',
      tone: 'cyan',
    },
    fitting: {
      title: 'Put it on',
      lead: 'Adjust until every contact touches skin. The diagram updates with each reading, and the next step is always the one that matters most.',
      pill: 'Connected',
      tone: 'green',
    },
    stale: {
      title: 'Waiting for a fresh reading',
      lead: 'No reading has arrived for a while. The last one is still shown, but it does not count as current.',
      pill: 'Stale reading',
      tone: 'amber',
    },
    ready: {
      title: 'Ready to record',
      lead: `All contacts checked and the live signal is steady. When you start, keep your eyes closed and stay still for ${RECORD_LENGTH_TEXT}.`,
      pill: 'Ready',
      tone: 'green',
    },
    recording: {
      title: 'Recording',
      lead: 'Guidance is paused. Nothing on screen changes until the recording ends.',
      pill: 'Recording',
      tone: 'cyan',
    },
    journal: {
      title: 'How are you right now?',
      lead: 'Optional. A few answers travel with the recording so a day can be read in context. Skip if you would rather not.',
      pill: 'Captured',
      tone: 'green',
    },
    processing: {
      title: 'Checking the recording',
      lead: `The ${RECORD_LENGTH_TEXT} are captured. Completeness, row count and contacts are checked now, before anything is sent.`,
      pill: 'Checking',
      tone: 'cyan',
    },
    kept: {
      title: 'Recording kept, not processed',
      lead: 'The recording did not pass the checks that run before processing. The reasons are listed below; nothing was re-dated or repaired.',
      pill: 'Kept',
      tone: 'amber',
    },
    sending: {
      title: 'Processing your recording',
      lead: 'The recording is being sent and scored. You can leave this page; it picks up where it left off.',
      pill: 'Processing',
      tone: 'cyan',
    },
    scored: {
      title: 'Your Daily Status is ready',
      // R46 (ruled 2026-09-30, owner delegation): the page is Report (R40).
      lead: 'Setup readiness helped the capture; the check that counts happened after the session. Your Daily Status appears on Report.',
      pill: 'Done',
      tone: 'green',
    },
    unscored: {
      title: 'Processed, without a Daily Status',
      lead: 'The recording was sent and the processor finished, but there is no result to show. What it said is below.',
      pill: 'No result',
      tone: 'amber',
    },
    lost:
      ctx.reason === 'stopped'
        ? {
            title: 'Stopped early',
            lead: 'You stopped the recording. What was captured is kept as an incomplete attempt and is never stitched to another.',
            pill: 'Stopped early',
            tone: 'amber',
          }
        : ctx.reason === 'package_refused'
          ? {
              title: 'Eyes-open recording not accepted',
              lead: 'The eyes-open recording did not pass the checks that run before anything is sent, so the paired session was given up and the eyes-closed recording stopped. Nothing was sent; nothing was repaired.',
              pill: 'Not accepted',
              tone: 'amber',
            }
          : {
              title: 'Connection lost',
              lead: 'The recording stopped when the headband disconnected. What was captured is kept as an incomplete attempt and is never stitched to another.',
              pill: 'Connection lost',
              tone: 'amber',
            },
  }
  return C[phase] ?? C.off
}

export type BadgeClass = '' | 'device-badge--busy' | 'device-badge--on' | 'device-badge--warn' | 'device-badge--live'

export interface Badge {
  cls: BadgeClass
  title: string
  sub: string
  short: string
}

export interface BadgeContext {
  battery?: number | null
  age?: number | null
  timer?: string
  reason?: LostReason | null
  synthetic?: boolean
}

/** Headband badge: dot colour repeated in words; glow only while recording. */
export function badgeFor(phase: Phase, ctx: BadgeContext = {}): Badge {
  const battery = typeof ctx.battery === 'number' && Number.isFinite(ctx.battery) ? `Headband · ${ctx.battery}%` : 'Headband'
  const age = ctx.age
  const timer = ctx.timer ?? RECORD_TIMER_TEXT
  const synthetic = ctx.synthetic ? ' · synthetic headband' : ''
  const B: Record<Phase, Badge> = {
    off: { cls: '', title: 'Headband', sub: 'Not connected' + synthetic, short: 'Not connected' },
    unsupported: { cls: 'device-badge--warn', title: 'Headband', sub: 'Not supported in this browser', short: 'Unsupported' },
    scanning: { cls: 'device-badge--busy', title: 'Headband', sub: 'Looking…' + synthetic, short: 'Looking…' },
    fitting: { cls: 'device-badge--on', title: battery, sub: 'Connected · fitting' + synthetic, short: 'Connected' },
    stale: {
      cls: 'device-badge--warn',
      title: battery,
      sub: (age == null ? 'No fresh reading' : `Last reading ${age} s ago`) + synthetic,
      short: 'Stale',
    },
    ready: { cls: 'device-badge--on', title: battery, sub: 'Live signal ready' + synthetic, short: 'Ready' },
    recording: { cls: 'device-badge--live', title: `Recording · ${timer}`, sub: 'eyes closed' + synthetic, short: timer },
    journal: { cls: 'device-badge--on', title: battery, sub: 'Connected · captured' + synthetic, short: 'Captured' },
    processing: { cls: 'device-badge--on', title: battery, sub: 'Connected · checking' + synthetic, short: 'Checking' },
    kept: { cls: 'device-badge--warn', title: 'Headband', sub: 'Disconnected · kept' + synthetic, short: 'Kept' },
    sending: { cls: 'device-badge--on', title: 'Headband', sub: 'Disconnected · processing' + synthetic, short: 'Processing' },
    scored: { cls: 'device-badge--on', title: 'Headband', sub: 'Disconnected · done' + synthetic, short: 'Done' },
    unscored: { cls: 'device-badge--warn', title: 'Headband', sub: 'Disconnected · no result' + synthetic, short: 'No result' },
    lost: {
      cls: 'device-badge--warn',
      title: 'Headband',
      sub: (ctx.reason === 'stopped' ? 'Stopped early' : ctx.reason === 'package_refused' ? 'Pair given up' : 'Connection lost') + synthetic,
      short: ctx.reason === 'stopped' ? 'Stopped' : ctx.reason === 'package_refused' ? 'Not accepted' : 'Lost',
    },
  }
  return B[phase] ?? B.off
}

export type StepState = 'done' | 'now' | 'warn' | 'pending'

export interface Step {
  label: string
  state: StepState
  n: string
}

/** The step rail: six steps, each done / now / warn / pending. */
export function steps(phase: Phase, ctx: { reason?: LostReason | null } = {}): Step[] {
  const idx: Record<Phase, number> = {
    off: -1,
    unsupported: -1,
    scanning: 0,
    fitting: 1,
    stale: 1,
    ready: 2,
    recording: 3,
    journal: 4,
    processing: 4,
    kept: 4,
    sending: 5,
    scored: 6,
    unscored: 5,
    lost: 3,
  }
  const at = idx[phase]
  return STEPS.map((label, i) => {
    let state: StepState = i < at ? 'done' : i === at ? 'now' : 'pending'
    if (phase === 'stale' && i === 1) state = 'warn'
    // Step 0 (Connected) is only at fault when the headband was lost; Stop early
    // and a refused block 1 happened with the connection intact.
    if (phase === 'lost' && (i === 3 || (i === 0 && ctx.reason !== 'stopped' && ctx.reason !== 'package_refused'))) state = 'warn'
    if ((phase === 'kept' || phase === 'unscored') && i === 5) state = 'warn'
    return { label, state, n: state === 'done' ? '✓' : String(i + 1) }
  })
}

export { WORDS } from './quality'
